import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export interface User {
  id: string;
  username: string;
  is_admin: boolean;
  created_at: string;
  last_login_at: string | null;
  include_images: boolean;
}

interface Credentials {
  username: string;
  password: string;
}

async function fetchMe(): Promise<User | null> {
  const res = await fetch("/api/v1/auth/me", { credentials: "same-origin" });
  if (!res.ok) return null;
  return res.json();
}

async function postCredentials(path: string, body: Credentials): Promise<User> {
  const res = await fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const detail = await res
      .json()
      .then((j) => (typeof j?.detail === "string" ? j.detail : null))
      .catch(() => null);
    throw new Error(detail ?? `${path} failed: ${res.status}`);
  }
  return res.json();
}

async function postLogout(): Promise<void> {
  await fetch("/api/v1/auth/logout", {
    method: "POST",
    credentials: "same-origin",
  });
}

export function useCurrentUser() {
  return useQuery<User | null>({
    queryKey: ["auth", "me"],
    queryFn: fetchMe,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

export function useRegister() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (creds: Credentials) => postCredentials("/api/v1/auth/register", creds),
    onSuccess: (user) => qc.setQueryData(["auth", "me"], user),
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (creds: Credentials) => postCredentials("/api/v1/auth/login", creds),
    onSuccess: (user) => qc.setQueryData(["auth", "me"], user),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: postLogout,
    onSuccess: () => {
      qc.setQueryData(["auth", "me"], null);
      // Reset anything that depended on the signed-in identity. For Phase C
      // that's just /me; Phase D will add blacklist/saved-searches queries.
      qc.invalidateQueries({ queryKey: ["auth"] });
    },
  });
}
